import React, { useState, useRef, useContext } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import './PDFViewerLite.scss';
import Chevron from "../Icons/Chevron";
import Context from "../../../context";
// import workerSrc from 'pdfjs-dist/build/pdf.worker.entry';
// pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;
// Using the above method can cause the following warning in the console:
//
//  Warning: Setting up fake worker.
//
//  This means the PDF.js library failed to initialize a real Web Worker and is falling back to a fake worker that runs on the main thread.
//  This can negatively affect performance, especially with large PDFs.
// To fix this and avoid the warning use new line

// Reminder: update or delete `/public/pdf.worker.min.js` if pdfjs version changes
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const PDFViewerLite = ({ fileUrl, loading, setLoading }) => {
  const { viewMode } = useContext(Context);

  const [numPages, setNumPages] = useState(null);
  const [scale, setScale] = useState(viewMode === 'mobile' ? 0.5 : 0.75);
  const [currentPage, setCurrentPage] = useState(1);
  const [isDragging, setIsDragging] = useState(false);

  const contentRef = useRef(null);
  const dragStart = useRef({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });

  const onDocumentLoadSuccess = ({ numPages }) => {
    setLoading(false);
    setNumPages(numPages);
  };

  const goToPrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const goToNextPage = () => setCurrentPage((prev) => Math.min(prev + 1, numPages));

  const zoomIn = () => setScale(s => Math.min(s + 0.25, 3));
  const zoomOut = () => setScale(s => Math.max(s - 0.25, 0.5));

  const onMouseDown = (e) => {
    setIsDragging(true);
    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
      scrollLeft: contentRef.current.scrollLeft,
      scrollTop: contentRef.current.scrollTop,
    };
  };

  const onMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    contentRef.current.scrollLeft = dragStart.current.scrollLeft - dx;
    contentRef.current.scrollTop = dragStart.current.scrollTop - dy;
  };

  const onMouseUpOrLeave = () => setIsDragging(false);

  return (
    <div className={`pdf-viewer-container ${loading ? 'loading' : ''}`}>
      <div className={`pdf-nav-buttons ${loading ? 'loading' : ''}`}>
        <button onClick={goToPrevPage} disabled={currentPage === 1} className="chevron-btn">
          <Chevron direction="left" size="small" />
        </button>
        <span className="pdf-page-indicator">
          {currentPage} / {numPages || '--'}
        </span>
        <button onClick={goToNextPage} disabled={currentPage === numPages} className="chevron-btn">
          <Chevron direction="right" size="small" />
        </button>
        <div className="separator" />
        <div className="pdf-zoom-controls">
          <button onClick={zoomOut} disabled={scale === 0.5}>-</button>
          <span>{Math.round(scale * 100)}%</span>
          <button onClick={zoomIn} disabled={scale === 3}>+</button>
        </div>

      </div>
      <div
        className="pdf-content"
        ref={contentRef}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUpOrLeave}
        onMouseLeave={onMouseUpOrLeave}
      >
        <div className="pdf-inner">
          <Document
            file={fileUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={null}
            noData={null}
            options={{ cMapUrl: 'cmaps/', cMapPacked: true }}
            onLoadError={(error) => {
              setLoading(false);
              console.error('PDF load error:', error)
            }}
            onSourceError={(error) => {
              setLoading(false);
              console.error('PDF source error:', error)
            }}
          >
            <Page
              pageNumber={currentPage}
              scale={scale}
              renderAnnotationLayer={true}
              renderTextLayer={true}
            />
          </Document>
        </div>
      </div>
    </div>
  );
};

export default PDFViewerLite;